import { ApiRef, createApiRef } from '@backstage/core-plugin-api';

import {
  AccountType,
  AgentAccessRequest,
  AgentAccessRequestStatus,
  AgentDataDetailed,
  AgentListResponse,
  AgentEndpointData,
  AgentAccessReviewStatus,
  AgentAccessUpdateResponse,
  Massp4aiUseCase,
} from './agentgarden.objects';
import { SimulateRoleRequest, SimulateRoleResponse } from './dev-mode.types';
import { ContactData } from './global.objects';

/**
 * Agent Garden API reference for live version
 */
export const agentGardenApiRef: ApiRef<AgentGardenApi> =
  createApiRef<AgentGardenApi>({
    id: 'plugin.aiecosystem-agentgarden.api',
  });

/**
 * API Interface for Agent Garden network communication
 */
export interface AgentGardenApi {
  /**
   * GET all agents
   * @param limit - Maximum number of agents to return (default: 1000)
   * @returns Array of agents
   */
  getAgents(limit?: number): Promise<AgentListResponse>;

  /**
   * GET an agent by its ID
   * @param id - The ID of the agent to retrieve
   * @throws When the agent with the given ID does not exist
   * @returns The agent with the given ID
   */
  getAgent(id: string): Promise<AgentDataDetailed>;

  /**
   * GET all access requests for a specific agent
   * @param agentId - The ID of the agent
   * @returns Array of access requests
   */
  getAgentAccessRequests(agentId: string): Promise<AgentAccessRequest[]>;

  /**
   * GET availability of an agent name
   * @param name - The agent name to check (case-insensitive, whitespace-ignored)
   * @param excludeAgentId - Optional UUID of an agent to exclude from the check (used when editing)
   * @returns Whether the name is available
   */
  getAgentNameAvailability(
    name: string,
    excludeAgentId?: string,
  ): Promise<{ available: boolean }>;

  /**
   * GET a MASSP4AI AI use case via the `api/ai-use-case/get-by-id` route of the auth-gateway
   * @param useCaseId - The MASSP4AI use case ID (UUID)
   * @throws When the ID is invalid or MASSP4AI is unavailable
   * @returns The use case, or null if it does not exist in MASSP4AI
   */
  getMassp4aiUseCase(useCaseId: string): Promise<Massp4aiUseCase | null>;

  /**
   * POST Create a new access request for an agent
   * @param agentId - The ID of the agent
   * @param requestData - Details of the access request
   * @returns Created request information
   */
  createAccessRequest(
    agentId: string,
    requestData: {
      accountType: AccountType;
      accountId?: string;
      applicationId?: string;
      businessNeed: string;
      consumptionEstimate?: string;
      countries: string[];
      requesterName: string;
      requesterEmail: string;
      businessRegion: string;
      userPrincipalName: string;
    },
  ): Promise<{ requestId: string; status: string; message: string }>;

  /**
   * PUT Update an access request (approve, reject, retain, revoke, unsubscribe)
   * @param agentId - The ID of the agent
   * @param updateData - Request IDs, decision, and details
   * @returns Updated request information
   */
  updateAccessRequest(
    agentId: string,
    updateData: {
      requestIds: string[];
      decision: AgentAccessRequestStatus;
      decisionNotes: string;
    },
  ): Promise<AgentAccessUpdateResponse>;

  /**
   * PUT Unsubscribe from an agent
   * @param agentId - The ID of the agent
   * @param requestId - The ID of the access request
   * @param updateData - Decision and notes
   * @returns Unsubscription result
   */
  unsubscribeFromAgent(
    agentId: string,
    requestId: string,
    updateData: {
      decision: AgentAccessRequestStatus;
      decisionNotes: string;
    },
  ): Promise<{
    requestId: string;
    status: string;
    decidedAtUTC: string;
    message: string;
  }>;

  /**
   * GET Gateway endpoint details for an agent
   * @param agentId - The ID of the agent
   * @param agentService - The service of the agent (e.g. 'Databricks')
   * @throws When user doesn't have access or endpoint doesn't exist
   * @returns Gateway endpoint details including URL and rate limits
   */
  getGatewayEndpointDetails(
    agentId: string,
    agentService: string,
  ): Promise<AgentEndpointData>;

  /**
   * POST Deprecate an agent
   * @param agentId - The ID of the agent, which is going to be deprecated
   * @param formData - The deprecation data including agent name, deprecation duration in days, a deprecation reason and suggested alternatives
   */
  deprecateAgent(
    agentId: string,
    formData: {
      agentName: string;
      deprecationDurationDays: number | null;
      deprecationReason: string;
      alternatives: string;
    },
  ): Promise<{
    agentId: string;
    agentName: string;
    status: string;
    deprecationStartedAt: string;
    deprecationCompletesAt: string;
    message: string;
  }>;

  /**
   * PUT Update ownership assignments for an agent as part of a provider access review
   * @param agentId - The ID of the agent
   * @param formData - The reviewed/updated ownership assignments
   * @returns Updated ownership information
   */
  updateAgentOwnership(
    agentId: string,
    formData: {
      l3Manager: ContactData;
      aiProductOwner: ContactData;
      informationOwner: ContactData;
      technicalContacts: ContactData[];
    },
  ): Promise<{
    agentId: string;
    l3Manager: ContactData;
    aiProductOwner: ContactData;
    informationOwner: ContactData;
    technicalContacts: ContactData[];
    providerAccessReviewStatus: AgentAccessReviewStatus;
    agentAccessReviewStatus: AgentAccessReviewStatus;
    message: string;
  }>;

  /**
   * Dev-only simulation for roles, access status, lifecycle, and deprecation.
   * Returns 404 in PROD and 403 for non-whitelisted users.
   */
  simulateRole(request: SimulateRoleRequest): Promise<SimulateRoleResponse>;
}
